import { contentWords, normalizeFr, stemAll, tokenizeWords } from './tokenize-fr';

/**
 * Matching tolérant de mots-clés (spec §2.A) : accents et casse ignorés,
 * pluriels/féminins via racines, mots vides ignorés, ordre libre dans une
 * fenêtre courte. « balayage prix » trouve « le prix d'un balayage ».
 */

export interface KeywordMatcher {
  raw: string;
  normalized: string;
  /** Racines des mots pleins (ou de tous les mots si le mot-clé n'en a aucun). */
  stems: string[];
  /** Taille de fenêtre de tokens dans laquelle les racines doivent apparaître (mots vides compris). */
  window: number;
  /**
   * Nombre minimal de racines pour un match « souple » (densité, intertitres, intro) :
   * un mot-clé long (« logiciel de prise de rendez-vous institut de beauté ») est presque
   * toujours reformulé ; exiger toutes ses racines mesure la copie exacte, pas le ciblage.
   */
  looseMin: number;
}

export function buildKeywordMatcher(keyword: string): KeywordMatcher | null {
  const normalized = normalizeFr(keyword).trim();
  const tokens = tokenizeWords(normalized);
  if (tokens.length === 0) return null;
  const content = contentWords(tokens);
  const stems = stemAll(content.length > 0 ? content : tokens);
  const looseMin = stems.length >= 3 ? Math.max(2, stems.length - 1) : stems.length;
  return { raw: keyword, normalized, stems, window: stems.length * 2 + 3, looseMin };
}

/**
 * Occurrences non chevauchantes du mot-clé dans une suite de racines.
 * Retourne les index de début.
 */
export function findKeywordPositions(textStems: string[], matcher: KeywordMatcher, loose = false): number[] {
  const positions: number[] = [];
  const wanted = new Set(matcher.stems);
  const required = loose ? matcher.looseMin : matcher.stems.length;
  const n = textStems.length;
  let i = 0;
  while (i < n) {
    if (!wanted.has(textStems[i])) {
      i++;
      continue;
    }
    const end = Math.min(n, i + matcher.window);
    let last = i;
    let found = 0;
    for (const stem of matcher.stems) {
      let idx = -1;
      for (let j = i; j < end; j++) {
        if (textStems[j] === stem) {
          idx = j;
          break;
        }
      }
      if (idx === -1) continue;
      found++;
      if (idx > last) last = idx;
    }
    if (found >= required) {
      positions.push(i);
      i = last + 1;
    } else {
      i++;
    }
  }
  return positions;
}

export function countKeyword(textStems: string[], matcher: KeywordMatcher, loose = false): number {
  return findKeywordPositions(textStems, matcher, loose).length;
}

/** Racines d'un texte brut (pipeline complet). */
export function textStems(text: string): string[] {
  return stemAll(tokenizeWords(normalizeFr(text)));
}

export function textContainsKeyword(text: string, matcher: KeywordMatcher, loose = false): boolean {
  return countKeyword(textStems(text), matcher, loose) > 0;
}

/** Occurrences de la phrase EXACTE (normalisée), bornées par des non-lettres. */
export function countExactPhrase(normalizedText: string, normalizedPhrase: string): number {
  if (normalizedPhrase.length === 0) return 0;
  const escaped = normalizedPhrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\s+/g, "[\\s'-]+");
  const re = new RegExp(`(^|[^a-z0-9])${escaped}(?=$|[^a-z0-9])`, 'g');
  let count = 0;
  while (re.exec(normalizedText) !== null) count++;
  return count;
}

/**
 * Part d'une liste de termes retrouvés dans le texte (matching tolérant).
 * Retourne aussi les termes manquants pour l'affichage.
 */
export function termCoverage(
  stems: string[],
  terms: string[],
  loose = false,
): { ratio: number; found: string[]; missing: string[] } {
  const found: string[] = [];
  const missing: string[] = [];
  for (const term of terms) {
    const matcher = buildKeywordMatcher(term);
    if (!matcher) continue;
    if (countKeyword(stems, matcher, loose) > 0) found.push(term);
    else missing.push(term);
  }
  const total = found.length + missing.length;
  return { ratio: total === 0 ? 1 : found.length / total, found, missing };
}

/**
 * Une question est « traitée » si ≥ `ratio` de ses mots pleins apparaissent
 * dans un même bloc (titre, phrase, item). Retourne true au premier bloc qui matche.
 */
export function questionCoveredBy(blocksStems: string[][], question: string, ratio: number): boolean {
  const qTokens = contentWords(tokenizeWords(normalizeFr(question)));
  if (qTokens.length === 0) return false;
  const qStems = new Set(stemAll(qTokens));
  const needed = Math.max(1, Math.ceil(qStems.size * ratio));
  for (const stems of blocksStems) {
    const set = new Set(stems);
    let hits = 0;
    for (const s of qStems) if (set.has(s)) hits++;
    if (hits >= needed) return true;
  }
  return false;
}
